import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../design_system/Button';
import { Input } from '../../design_system/forms/Input';
import { Icon } from '../../design_system/Icon';
import styled from 'astroturf/react';

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const Badge = styled.span`
  background-color: #0366d6;
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
  margin-left: 6px;

  &.open {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 320px;
  background-color: white;
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(149, 157, 165, 0.2);
  z-index: 1000;
  padding: 16px;
  opacity: 1;
  transform: translateY(0);
  transition: all 0.2s ease;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e1e4e8;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #24292e;
`;

const FilterGroup = styled.div`
  margin-bottom: 20px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e1e4e8;
`;

interface FilterPanelProps {
  onFilterChange?: (settings: FilterSettings) => void;
  currentFilters?: FilterSettings;
}

export interface FilterSettings {
  relevance: {
    min: number;
  };
  journalImpactFactor: {
    min: number;
  };
  citations: {
    min: number;
  };
  sources: {
    selected: (typeof AVAILABLE_SOURCES)[number]['value'][];
  };
  excludePreprints: {
    enabled: boolean;
  };
}

// Available source options
export const AVAILABLE_SOURCES = [
  { value: 'refutation_finder', label: 'Academia Search' },
  { value: 'web_search_citations', label: 'Web Search' },
  { value: 'search_agent', label: 'Search Agent' },
  { value: 'agentful_web_search_citations', label: 'Agentic Web Search' },
  // Include mock_search for development/testing
  ...(process.env.NODE_ENV !== 'production'
    ? [{ value: 'mock_search', label: 'Mock Search (Dev)' }]
    : []),
];

const FilterPanel: React.FC<FilterPanelProps> = ({
  onFilterChange,
  currentFilters,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // String values for input binding (allows natural editing)
  const [relevanceStr, setRelevanceStr] = useState(
    currentFilters?.relevance.min.toString() || '0',
  );
  const [journalImpactFactorStr, setJournalImpactFactorStr] = useState(
    currentFilters?.journalImpactFactor.min.toString() || '0',
  );
  const [citationsStr, setCitationsStr] = useState(
    currentFilters?.citations.min.toString() || '0',
  );
  const [selectedSources, setSelectedSources] = useState<string[]>(
    currentFilters?.sources.selected || AVAILABLE_SOURCES.map((s) => s.value),
  );
  const [excludePreprints, setExcludePreprints] = useState(
    currentFilters?.excludePreprints?.enabled || false,
  );

  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Handle keyboard navigation and click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        buttonRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        // Return focus to the button that opened the panel
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);

      // Focus the first focusable element in the panel
      setTimeout(() => {
        const firstInput = panelRef.current?.querySelector('input');
        firstInput?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  const handleRelevanceChange = (value: string) => {
    setRelevanceStr(value);
  };

  const handleJournalImpactFactorChange = (value: string) => {
    setJournalImpactFactorStr(value);
  };

  const handleCitationsChange = (value: string) => {
    setCitationsStr(value);
  };

  const handleSourceToggle = (sourceValue: string) => {
    setSelectedSources((prev) => {
      if (prev.includes(sourceValue)) {
        return prev.filter((s) => s !== sourceValue);
      } else {
        return [...prev, sourceValue];
      }
    });
  };

  // Count active filters
  const getActiveFilterCount = () => {
    const allSourcesSelected =
      selectedSources.length === AVAILABLE_SOURCES.length;
    return (
      ((parseFloat(relevanceStr) || 0) > 0 ? 1 : 0) +
      ((parseFloat(journalImpactFactorStr) || 0) > 0 ? 1 : 0) +
      ((parseInt(citationsStr) || 0) > 0 ? 1 : 0) +
      (allSourcesSelected ? 0 : 1) +
      (excludePreprints ? 1 : 0)
    );
  };

  return (
    <Container>
      {/* Filter Button */}
      <Button
        ref={buttonRef}
        onClick={togglePanel}
        size="xs"
        icon={<Icon name="tune" size={16} />}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={`Filter results (${getActiveFilterCount()} active filters)`}
      >
        <span>Filter Results</span>
        <Badge className={isOpen ? 'open' : ''}>{getActiveFilterCount()}</Badge>
      </Button>

      {/* Filter Panel */}
      {isOpen && (
        <Panel
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="filter-panel-title"
        >
          {/* Panel Header */}
          <Header>
            <Title id="filter-panel-title">Filter Results</Title>
            <Button
              onClick={() => {
                setIsOpen(false);
                // Return focus to the main filter button
                setTimeout(() => buttonRef.current?.focus(), 10);
              }}
              size="xs"
              icon={<Icon name="close" size={16} />}
              aria-label="Close filter panel"
            />
          </Header>

          {/* Relevance Filter */}
          <FilterGroup>
            <Input
              type="number"
              step="1"
              min="0"
              max="100"
              value={relevanceStr}
              onChange={(e) => handleRelevanceChange(e.target.value)}
              placeholder="Min %"
              label="Relevance to claim (minimum %)"
            />
          </FilterGroup>

          {/* Citations Filter */}
          <FilterGroup>
            <Input
              type="number"
              min="0"
              value={citationsStr}
              onChange={(e) => handleCitationsChange(e.target.value)}
              placeholder="Min"
              label="Number of Citations (minimum)"
            />
          </FilterGroup>

          {/* Journal Impact Factor Filter */}
          <FilterGroup>
            <Input
              type="number"
              step="0.1"
              min="0"
              value={journalImpactFactorStr}
              onChange={(e) => handleJournalImpactFactorChange(e.target.value)}
              placeholder="Min"
              label="Journal Impact Factor (minimum)"
            />
          </FilterGroup>

          {/* Source Filter */}
          <FilterGroup>
            <label
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#24292e',
                marginBottom: '8px',
                display: 'block',
              }}
            >
              Data Sources
            </label>
            {AVAILABLE_SOURCES.map((source) => (
              <div
                key={source.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                }}
              >
                <input
                  type="checkbox"
                  id={`source-${source.value}`}
                  checked={selectedSources.includes(source.value)}
                  onChange={() => handleSourceToggle(source.value)}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                  }}
                />
                <label
                  htmlFor={`source-${source.value}`}
                  style={{
                    fontSize: '14px',
                    cursor: 'pointer',
                    color: '#24292e',
                  }}
                >
                  {source.label}
                </label>
              </div>
            ))}
          </FilterGroup>

          {/* Exclude Preprints Filter */}
          <FilterGroup>
            <label
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#24292e',
                marginBottom: '8px',
                display: 'block',
              }}
            >
              Publication Status
            </label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <input
                type="checkbox"
                id="exclude-preprints"
                checked={excludePreprints}
                onChange={(e) => setExcludePreprints(e.target.checked)}
                style={{
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer',
                }}
              />
              <label
                htmlFor="exclude-preprints"
                style={{
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: '#24292e',
                }}
              >
                Exclude preprints
              </label>
            </div>
          </FilterGroup>

          {/* Action Buttons */}
          <ActionButtons>
            <Button
              onClick={() => {
                setRelevanceStr('0');
                setJournalImpactFactorStr('0');
                setCitationsStr('0');
                setSelectedSources(AVAILABLE_SOURCES.map((s) => s.value));
                setExcludePreprints(false);
                if (onFilterChange) {
                  onFilterChange({
                    relevance: { min: 0 },
                    journalImpactFactor: { min: 0 },
                    citations: { min: 0 },
                    sources: {
                      selected: AVAILABLE_SOURCES.map((s) => s.value),
                    },
                    excludePreprints: {
                      enabled: false,
                    },
                  });
                }
              }}
              style={{ flex: 1 }}
              size="sm"
              secondary
            >
              Clear All
            </Button>
            <Button
              onClick={() => {
                if (onFilterChange) {
                  onFilterChange({
                    relevance: {
                      min: parseFloat(relevanceStr) || 0,
                    },
                    journalImpactFactor: {
                      min: parseFloat(journalImpactFactorStr) || 0,
                    },
                    citations: { min: parseInt(citationsStr) || 0 },
                    sources: { selected: selectedSources },
                    excludePreprints: {
                      enabled: excludePreprints,
                    },
                  });
                }
                setIsOpen(false);
                // Return focus to the main filter button
                setTimeout(() => buttonRef.current?.focus(), 10);
              }}
              style={{ flex: 1 }}
              size="sm"
            >
              Apply
            </Button>
          </ActionButtons>
        </Panel>
      )}
    </Container>
  );
};

export default FilterPanel;
